import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { APP_CONFIG } from '../config/app.config';

interface WelcomeDialogProps {
  open: boolean;
  onClose: () => void;
}

const WelcomeDialog: React.FC<WelcomeDialogProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(2);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!open) {
      setCountdown(2);
      setIsClosing(false);
      return;
    }
    
    // 🎯 立即重置倒數計時，確保視窗打開時立即顯示 2
    setCountdown(2);
    setIsClosing(false);
    
    // 🎯 優化：使用更精確的倒數邏輯
    let currentCount = 2;
    
    const timer = setInterval(() => {
      currentCount -= 1;
      
      if (currentCount <= 0) {
        clearInterval(timer);
        setIsClosing(true);
        setCountdown(0);
        // 🎯 立即關閉，不使用 setTimeout
        onClose();
      } else {
        setCountdown(currentCount);
      }
    }, 1000);
    
    return () => {
      clearInterval(timer);
      setIsClosing(false);
    };
  }, [open]); // 🎯 移除 onClose 依賴項，避免不必要的重新執行

  return (
    <Dialog
      open={open && !isClosing}
      onClose={onClose}
      maxWidth={false}
      TransitionProps={{
        timeout: 300
      }}
      PaperProps={{
        style: {
          borderRadius: 32,
          width: '400px',
          maxWidth: '90vw'
        }
      }}
    >
      <DialogContent>
        <Box sx={{ textAlign: 'center', pt: 4, px: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <img
              src="/logo.png"
              alt="Check Play Logo"
              style={{ width: '56px', height: 'auto' }}
            />
          </Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontSize: '2rem',
              color: '#666666',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              mb: 6.25
            }}
          >
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ color: '#FF0000' }}>簡播</Box>
              <Box component="span" sx={{ color: '#00B0FF' }}>CHECK</Box>
              <Box component="span" sx={{ color: '#FF0000' }}>PLAY</Box>
            </Box>
            <Box component="span" sx={{ fontSize: '1.2rem', color: '#666666' }}>
              讓「播放」變有趣
            </Box>
            <Box component="span" sx={{ fontSize: '1.2rem', color: '#666666' }}>
              讓「知識」變有趣
            </Box>
            <Box component="span" sx={{ fontSize: '1.2rem', color: '#666666' }}>
              讓「分享」變有趣
            </Box>
            <Box component="span" sx={{ fontSize: '1.2rem', color: '#666666' }}>
              Make your own #checkpoint
            </Box>
          </Typography>
          <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mt: 2 }}>
            版本：{APP_CONFIG.version}
          </Typography>
          <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mt: 1 }}>
            Copyright © 2025 Red Ball Studio
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: '0.7rem', color: '#666666', mb: 0.5 }}
          >
            <Link
              href="mailto:jeff@redball.com.tw"
              sx={{
                color: '#666666',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              jeff@redball.com.tw
            </Link>
          </Typography>
          <Typography variant="h6" align="center" sx={{ color: '#1976d2', mt: 2, mb: 0 }}>
            {countdown > 0 ? `視窗將於 ${countdown} 秒後自動關閉` : '歡迎使用！'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 4, gap: 2 }}>
        <Button
          onClick={() => {
            navigate('/main');
            onClose();
          }}
          variant="contained"
          sx={{
            minWidth: '120px',
            py: 1,
            fontSize: '1.1rem',
            borderRadius: 1.5
          }}
        >
          首頁
        </Button>
        <Button
          onClick={() => {
            navigate('/favorite');
            onClose();
          }}
          variant="contained"
          sx={{
            minWidth: '120px',
            py: 1,
            fontSize: '1.1rem',
            borderRadius: 1.5,
            backgroundColor: '#FF0000',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#d90000',
              color: '#fff'
            }
          }}
        >
          瀏覽
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeDialog; 